@if (riskProfile(); as profile) {
  <div class="mc-shell">
    <!-- ── Header ── -->
    <header class="mc-header">
      <div class="mc-eyebrow">
        <mat-icon aria-hidden="true">insights</mat-icon>
        <span>{{ 'model_card.report_eyebrow' | transloco }}</span>
      </div>
      <h1 class="mc-doc-title">{{ assessmentName() }}</h1>
      @if (headlineVerdictDisplay()) {
        <p class="mc-verdict">{{ headlineVerdictDisplay() }}</p>
      }
      <div class="mc-header-chips">
        @if (issuedDate()) {
          <span class="mc-header-chip">
            <mat-icon aria-hidden="true">schedule</mat-icon>
            <span>{{ 'model_card.issued_label' | transloco }}</span>
            <strong>{{ issuedDate() }}</strong>
          </span>
        }
        <span class="mc-header-chip">
          <mat-icon aria-hidden="true">description</mat-icon>
          <strong>{{ obligationCount() }}</strong>
          <span>{{ 'model_card.obligations_word' | transloco }}</span>
        </span>
      </div>
    </header>

    <!-- Risk Level Menus (super-manager edit mode) -->
    <mat-menu #aiRiskMenu="matMenu">
      @for (level of AI_RISK_LEVELS; track level) {
        <button mat-menu-item (click)="setAiRiskLevel(level)">
          <app-badge
            [text]="level"
            [badgeClass]="badgeClassPrefix + getClassificationColor(level)"
          />
        </button>
      }
    </mat-menu>
    <mat-menu #gdprMenu="matMenu">
      @for (level of RISK_LEVELS; track level) {
        <button mat-menu-item (click)="setGdprScore(level)">
          <app-badge [text]="level" [badgeClass]="badgeClassPrefix + getScoreColor(level)" />
        </button>
      }
    </mat-menu>
    <mat-menu #cybersecurityMenu="matMenu">
      @for (level of RISK_LEVELS; track level) {
        <button mat-menu-item (click)="setCybersecurityScore(level)">
          <app-badge [text]="level" [badgeClass]="badgeClassPrefix + getScoreColor(level)" />
        </button>
      }
    </mat-menu>

    <!-- ── 01 Executive summary ── -->
    <section class="mc-section">
      <header class="mc-section-head">
        <span class="mc-section-num">01</span>
        <h2 class="mc-section-title">{{ 'model_card.executive_summary' | transloco }}</h2>
      </header>
      <div class="mc-section-rule"></div>

      <div class="mc-summary-block">
        @if (isEditingSummary()) {
          <textarea
            [ngModel]="descEditValue()"
            (ngModelChange)="updateDescValue($event)"
            [placeholder]="'model_card.summary_placeholder' | transloco"
            [disabled]="isSavingDescriptions()"
            class="mc-textarea"
            rows="6"
          ></textarea>
          <div class="mc-inline-actions">
            <button
              type="button"
              class="mc-text-btn"
              (click)="cancelDescEdit()"
              [disabled]="isSavingDescriptions()"
            >
              <mat-icon aria-hidden="true">close</mat-icon>
              <span>{{ 'model_card.cancel' | transloco }}</span>
            </button>
            <button
              type="button"
              class="mc-text-btn mc-text-btn--primary"
              (click)="saveSummary()"
              [disabled]="isSavingDescriptions()"
            >
              <mat-icon aria-hidden="true">{{ isSavingDescriptions() ? 'sync' : 'save' }}</mat-icon>
              <span>{{
                (isSavingDescriptions() ? 'model_card.saving' : 'model_card.save_changes')
                  | transloco
              }}</span>
            </button>
          </div>
        } @else if (canEdit()) {
          <button
            type="button"
            class="mc-inline-text"
            (click)="startEditSummary()"
            [title]="'model_card.click_to_edit' | transloco"
          >
            <span class="mc-inline-text-body">{{
              summaryDisplay() || ('model_card.summary_placeholder' | transloco)
            }}</span>
            <mat-icon class="mc-inline-edit-hint" aria-hidden="true">edit</mat-icon>
          </button>
        } @else if (summaryDisplay()) {
          <p class="mc-summary-text">{{ summaryDisplay() }}</p>
        } @else {
          <p class="mc-summary-text mc-summary-text--muted">
            {{ 'model_card.empty_message' | transloco }}
          </p>
        }
      </div>
    </section>

    <!-- ── 02 Risk by framework ── -->
    <section class="mc-section">
      <header class="mc-section-head">
        <span class="mc-section-num">02</span>
        <h2 class="mc-section-title">{{ 'model_card.risk_by_framework' | transloco }}</h2>
        <span class="mc-section-hint">{{ 'model_card.select_framework_hint' | transloco }}</span>
      </header>

      @let editingLevels = canEdit();
      <div class="mc-framework-cards">
        <!-- EU AI Act -->
        <div
          class="mc-fw-card"
          role="button"
          tabindex="0"
          [class.is-active]="selectedCard() === 'AI_ACT'"
          (click)="selectCard('AI_ACT')"
          (keydown.enter)="selectCard('AI_ACT')"
          (keydown.space)="$event.preventDefault(); selectCard('AI_ACT')"
        >
          <span class="mc-fw-eyebrow">{{ 'model_card.framework_ai_act' | transloco }}</span>
          <button
            type="button"
            class="mc-fw-level"
            [class.mc-fw-level--editable]="editingLevels"
            [matMenuTriggerFor]="editingLevels ? aiRiskMenu : null"
            (click)="editingLevels ? $event.stopPropagation() : null"
          >
            <app-badge
              [text]="profile.aiActClassification"
              [badgeClass]="badgeClassPrefix + getClassificationColor(profile.aiActClassification)"
              [class.editable-badge]="editingLevels"
            />
            @if (editingLevels) {
              <mat-icon class="mc-fw-caret" aria-hidden="true">expand_more</mat-icon>
            }
          </button>
          <span class="mc-fw-status">
            <mat-icon aria-hidden="true">info</mat-icon>
            {{
              (selectedCard() === 'AI_ACT'
                ? 'model_card.showing_details'
                : 'model_card.view_details'
              ) | transloco
            }}
          </span>
        </div>

        <!-- GDPR -->
        <div
          class="mc-fw-card"
          role="button"
          tabindex="0"
          [class.is-active]="selectedCard() === 'GDPR'"
          (click)="selectCard('GDPR')"
          (keydown.enter)="selectCard('GDPR')"
          (keydown.space)="$event.preventDefault(); selectCard('GDPR')"
        >
          <span class="mc-fw-eyebrow">{{ 'model_card.framework_gdpr' | transloco }}</span>
          <button
            type="button"
            class="mc-fw-level"
            [class.mc-fw-level--editable]="editingLevels"
            [matMenuTriggerFor]="editingLevels ? gdprMenu : null"
            (click)="editingLevels ? $event.stopPropagation() : null"
          >
            <app-badge
              [text]="profile.gdprScore"
              [badgeClass]="badgeClassPrefix + getScoreColor(profile.gdprScore)"
              [class.editable-badge]="editingLevels"
            />
            @if (editingLevels) {
              <mat-icon class="mc-fw-caret" aria-hidden="true">expand_more</mat-icon>
            }
          </button>
          <span class="mc-fw-status">
            <mat-icon aria-hidden="true">info</mat-icon>
            {{
              (selectedCard() === 'GDPR' ? 'model_card.showing_details' : 'model_card.view_details')
                | transloco
            }}
          </span>
        </div>

        <!-- Cybersecurity -->
        <div
          class="mc-fw-card"
          role="button"
          tabindex="0"
          [class.is-active]="selectedCard() === 'CYBERSECURITY'"
          (click)="selectCard('CYBERSECURITY')"
          (keydown.enter)="selectCard('CYBERSECURITY')"
          (keydown.space)="$event.preventDefault(); selectCard('CYBERSECURITY')"
        >
          <span class="mc-fw-eyebrow">{{ 'model_card.framework_cybersecurity' | transloco }}</span>
          <button
            type="button"
            class="mc-fw-level"
            [class.mc-fw-level--editable]="editingLevels"
            [matMenuTriggerFor]="editingLevels ? cybersecurityMenu : null"
            (click)="editingLevels ? $event.stopPropagation() : null"
          >
            <app-badge
              [text]="profile.cybersecurityScore"
              [badgeClass]="badgeClassPrefix + getScoreColor(profile.cybersecurityScore)"
              [class.editable-badge]="editingLevels"
            />
            @if (editingLevels) {
              <mat-icon class="mc-fw-caret" aria-hidden="true">expand_more</mat-icon>
            }
          </button>
          <span class="mc-fw-status">
            <mat-icon aria-hidden="true">info</mat-icon>
            {{
              (selectedCard() === 'CYBERSECURITY'
                ? 'model_card.showing_details'
                : 'model_card.view_details'
              ) | transloco
            }}
          </span>
        </div>
      </div>

      <!-- Detail panel for the selected framework -->
      <div class="mc-fw-detail">
        <h3 class="mc-fw-detail-title">{{ selectedFrameworkFullName() }}</h3>
        @if (isEditingFramework()) {
          <textarea
            [ngModel]="descEditValue()"
            (ngModelChange)="updateDescValue($event)"
            [placeholder]="'model_card.description_placeholder' | transloco"
            [disabled]="isSavingDescriptions()"
            class="mc-textarea"
            rows="5"
          ></textarea>
          <div class="mc-inline-actions">
            <button
              type="button"
              class="mc-text-btn"
              (click)="cancelDescEdit()"
              [disabled]="isSavingDescriptions()"
            >
              <mat-icon aria-hidden="true">close</mat-icon>
              <span>{{ 'model_card.cancel' | transloco }}</span>
            </button>
            <button
              type="button"
              class="mc-text-btn mc-text-btn--primary"
              (click)="saveFramework()"
              [disabled]="isSavingDescriptions()"
            >
              <mat-icon aria-hidden="true">{{ isSavingDescriptions() ? 'sync' : 'save' }}</mat-icon>
              <span>{{
                (isSavingDescriptions() ? 'model_card.saving' : 'model_card.save_changes')
                  | transloco
              }}</span>
            </button>
          </div>
        } @else if (canEdit()) {
          <button
            type="button"
            class="mc-inline-text mc-inline-text--detail"
            (click)="startEditFramework()"
            [title]="'model_card.click_to_edit' | transloco"
          >
            <span class="mc-inline-text-body">{{
              selectedDescriptionDisplay() || ('model_card.description_placeholder' | transloco)
            }}</span>
            <mat-icon class="mc-inline-edit-hint" aria-hidden="true">edit</mat-icon>
          </button>
        } @else if (selectedDescriptionDisplay()) {
          <p class="mc-fw-detail-text">{{ selectedDescriptionDisplay() }}</p>
        } @else {
          <p class="mc-fw-detail-text mc-summary-text--muted">—</p>
        }
      </div>
    </section>

    <!-- ── 03 Obligations ── -->
    <section class="mc-section">
      <header class="mc-section-head">
        <span class="mc-section-num">03</span>
        <h2 class="mc-section-title">{{ 'model_card.obligations_title' | transloco }}</h2>
        <span class="mc-section-hint">
          {{ 'model_card.obligations_identified' | transloco: { count: obligationCount() } }}
        </span>
      </header>
      <p class="mc-obligations-lede">{{ 'model_card.obligations_lede' | transloco }}</p>

      @for (category of categoryOrder; track category) {
        @let items = itemsForCategory(category);
        @if (items.length > 0 || canEdit()) {
          <div class="mc-ob-group">
            <div class="mc-ob-group-head">
              <span class="mc-ob-group-title">
                {{
                  (category === 'PRE_DEPLOYMENT'
                    ? 'model_card.group_pre_deployment'
                    : 'model_card.group_general'
                  ) | transloco
                }}
              </span>
              @if (items.length > 0) {
                <span class="mc-ob-group-count">{{ items.length }}</span>
              }
            </div>
            <p class="mc-ob-group-hint">
              {{
                (category === 'PRE_DEPLOYMENT'
                  ? 'model_card.group_pre_deployment_hint'
                  : 'model_card.group_general_hint'
                ) | transloco
              }}
            </p>

            @for (item of items; track item.index; let i = $index) {
              @if (isEditingObligation(item.index)) {
                <!-- ── Inline edit card ── -->
                <article class="mc-ob-card mc-ob-card--editing">
                  <span class="mc-field-label">{{
                    'model_card.obligation_label' | transloco
                  }}</span>
                  <textarea
                    [ngModel]="item.obligation.text"
                    (ngModelChange)="updateObligationText(item.index, $event)"
                    [placeholder]="'model_card.obligation_placeholder' | transloco"
                    [disabled]="isSavingObligations()"
                    class="mc-textarea"
                    rows="2"
                  ></textarea>

                  <span class="mc-field-label">{{
                    'model_card.triggering_finding' | transloco
                  }}</span>
                  <textarea
                    [ngModel]="item.obligation.triggering_finding || ''"
                    (ngModelChange)="updateObligationFinding(item.index, $event)"
                    [placeholder]="'model_card.finding_placeholder' | transloco"
                    [disabled]="isSavingObligations()"
                    class="mc-textarea"
                    rows="2"
                  ></textarea>

                  <span class="mc-field-label">{{ 'model_card.steps' | transloco }}</span>
                  <textarea
                    [ngModel]="item.obligation.steps || ''"
                    (ngModelChange)="updateObligationSteps(item.index, $event)"
                    [placeholder]="'model_card.steps_placeholder' | transloco"
                    [disabled]="isSavingObligations()"
                    class="mc-textarea"
                    rows="3"
                  ></textarea>

                  <span class="mc-field-label">{{ 'model_card.links' | transloco }}</span>
                  <div class="mc-links-edit">
                    @for (link of item.obligation.links; track $index; let linkIdx = $index) {
                      <div class="mc-link-row">
                        <input
                          type="text"
                          [ngModel]="link"
                          (ngModelChange)="updateObligationLink(item.index, linkIdx, $event)"
                          [placeholder]="'model_card.link_placeholder' | transloco"
                          [disabled]="isSavingObligations()"
                          class="mc-input"
                        />
                        <button
                          type="button"
                          class="mc-icon-btn"
                          (click)="removeObligationLink(item.index, linkIdx)"
                          [disabled]="isSavingObligations()"
                          [title]="'model_card.remove_link' | transloco"
                        >
                          <mat-icon aria-hidden="true">close</mat-icon>
                        </button>
                      </div>
                    }
                    <button
                      type="button"
                      class="mc-text-btn"
                      (click)="addObligationLink(item.index)"
                      [disabled]="isSavingObligations()"
                    >
                      <mat-icon aria-hidden="true">add_link</mat-icon>
                      <span>{{ 'model_card.add_link' | transloco }}</span>
                    </button>
                  </div>

                  <div class="mc-ob-edit-footer">
                    <button
                      type="button"
                      class="mc-text-btn mc-text-btn--danger"
                      (click)="deleteObligation(item.index)"
                      [disabled]="isSavingObligations()"
                    >
                      <mat-icon aria-hidden="true">delete</mat-icon>
                      <span>{{ 'model_card.remove_obligation' | transloco }}</span>
                    </button>
                    <div class="mc-ob-edit-footer-end">
                      <button
                        type="button"
                        class="mc-text-btn"
                        (click)="cancelEditObligation()"
                        [disabled]="isSavingObligations()"
                      >
                        <mat-icon aria-hidden="true">close</mat-icon>
                        <span>{{ 'model_card.cancel' | transloco }}</span>
                      </button>
                      <button
                        type="button"
                        class="mc-text-btn mc-text-btn--primary"
                        (click)="saveObligation()"
                        [disabled]="isSavingObligations()"
                      >
                        <mat-icon aria-hidden="true">{{
                          isSavingObligations() ? 'sync' : 'save'
                        }}</mat-icon>
                        <span>{{
                          (isSavingObligations() ? 'model_card.saving' : 'model_card.save_changes')
                            | transloco
                        }}</span>
                      </button>
                    </div>
                  </div>
                </article>
              } @else {
                <!-- ── Read card ── -->
                <article class="mc-ob-card">
                  <div class="mc-ob-head">
                    <span class="mc-ob-num">{{ i + 1 < 10 ? '0' + (i + 1) : i + 1 }}</span>
                    <h3 class="mc-ob-title">{{ item.obligation.text }}</h3>
                    @if (canEdit()) {
                      <button
                        type="button"
                        class="mc-ob-edit-btn"
                        (click)="startEditObligation(item.index)"
                        [title]="'model_card.edit_obligations' | transloco"
                        [attr.aria-label]="'model_card.edit_obligations' | transloco"
                      >
                        <mat-icon aria-hidden="true">edit</mat-icon>
                      </button>
                    }
                  </div>

                  @if (item.obligation.triggering_finding) {
                    <p class="mc-ob-why">
                      <span class="mc-ob-why-label">{{ 'model_card.why_label' | transloco }}</span>
                      <span class="mc-ob-why-sep">·</span>
                      {{ item.obligation.triggering_finding }}
                    </p>
                  }

                  @if (item.obligation.steps) {
                    <p class="mc-ob-steps">{{ item.obligation.steps }}</p>
                  }

                  @if (item.obligation.links && item.obligation.links.length > 0) {
                    <div class="mc-ob-links">
                      @for (link of item.obligation.links; track link) {
                        <a
                          class="mc-ob-link"
                          [href]="link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <mat-icon aria-hidden="true">open_in_new</mat-icon>
                          <span>{{ link }}</span>
                        </a>
                      }
                    </div>
                  }
                </article>
              }
            }

            @if (canEdit()) {
              <button
                type="button"
                class="mc-ob-add-btn"
                (click)="addObligation(category)"
                [disabled]="isSavingObligations() || editingIndex() !== null"
              >
                <mat-icon aria-hidden="true">add</mat-icon>
                <span>{{ 'model_card.add_obligation' | transloco }}</span>
              </button>
            }
          </div>
        }
      }
    </section>
  </div>
} @else {
  <div class="mc-shell mc-shell--empty">
    <app-empty-state
      variant="panel"
      icon="assessment"
      [title]="'model_card.empty_title' | transloco"
      [message]="'model_card.empty_message' | transloco"
    />
  </div>
}
